import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { KeyRound, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

// A7b (I.160): senha da conferência para quem tem a chave — situação (bloqueio, tentativas, quem entrou),
// "Gerar nova senha" e "Ligar senha" (só antes da primeira contagem). Os 6 números aparecem uma única vez.

interface PasswordStatus {
  success: boolean;
  error?: string;
  password_required: boolean;
  has_password: boolean;
  blocked: boolean;
  attempts_left: number | null;
  entries: { user_id: string; name: string | null; entered_at: string }[];
}

interface GenerateResult {
  success: boolean;
  error?: string;
  password?: string;
  replaced?: boolean;
}

/** Gera (ou troca) a senha pelo banco; devolve os 6 números, que a tela mostra uma única vez. */
export async function generateStockChecklistPassword(token: string): Promise<string> {
  const { data, error } = await supabase.rpc("generate_stock_checklist_password", { p_token: token });
  if (error) throw error;
  const result = data as unknown as GenerateResult;
  if (!result.success || !result.password) throw new Error(result.error ?? "Não foi possível gerar a senha");
  return result.password;
}

interface StockChecklistPasswordRevealProps {
  password: string | null;
  onClose: () => void;
  /** Ação extra no clique (ex.: abrir a conferência em nova aba, direto no clique) */
  extraAction?: { label: string; onClick: () => void };
}

export const StockChecklistPasswordReveal = ({ password, onClose, extraAction }: StockChecklistPasswordRevealProps) => (
  <Dialog open={!!password} onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent className="sm:max-w-sm">
      <DialogHeader>
        <DialogTitle>Senha da conferência</DialogTitle>
        <DialogDescription>
          Anote agora: a senha aparece só esta vez. Quem for contar sem a permissão de conferência entra com o próprio
          login, abre o link da conferência e digita estes 6 números.
        </DialogDescription>
      </DialogHeader>
      <p className="text-center text-4xl font-mono font-bold tracking-[0.4em] py-4 select-all">{password}</p>
      <DialogFooter className="gap-2">
        {extraAction && (
          <Button variant="outline" onClick={extraAction.onClick}>{extraAction.label}</Button>
        )}
        <Button onClick={onClose}>Anotei</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

interface StockChecklistPasswordPanelProps {
  token: string;
  passwordRequired: boolean;
  /** Ligar a senha só antes da primeira contagem (o banco também recusa depois) */
  canTurnOn: boolean;
  onChanged: () => void;
}

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export const StockChecklistPasswordPanel = ({ token, passwordRequired, canTurnOn, onChanged }: StockChecklistPasswordPanelProps) => {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);

  const { data: status } = useQuery({
    queryKey: ["stock-check-password-status", token],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_stock_checklist_password_status", { p_token: token });
      if (error) throw error;
      return data as unknown as PasswordStatus;
    },
    enabled: passwordRequired,
  });

  const generateMutation = useMutation({
    mutationFn: () => generateStockChecklistPassword(token),
    onSuccess: (password) => {
      setConfirmOpen(false);
      setRevealed(password);
      queryClient.invalidateQueries({ queryKey: ["stock-check-password-status", token] });
      onChanged();
    },
    onError: (err) => {
      setConfirmOpen(false);
      toast.error((err as { message?: string })?.message || "Não foi possível gerar a senha");
    },
  });

  if (!passwordRequired && !canTurnOn) return null;

  return (
    <div className="mt-2 rounded-lg border border-border bg-muted/40 p-3 text-xs space-y-2">
      {passwordRequired ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="gap-1"><KeyRound className="h-3 w-3" /> Conferência com senha</Badge>
            {status?.success && (status.blocked ? (
              <Badge variant="outline" className="gap-1 bg-red-500/10 text-red-700 border-red-500/30">
                <Lock className="h-3 w-3" /> Senha bloqueada
              </Badge>
            ) : status.attempts_left != null ? (
              <span className="text-muted-foreground">Tentativas restantes: {status.attempts_left}</span>
            ) : null)}
            <Button
              variant="outline"
              size="sm"
              className="h-7 ml-auto"
              onClick={() => setConfirmOpen(true)}
              disabled={generateMutation.isPending}
            >
              Gerar nova senha
            </Button>
          </div>
          {status?.success && (
            <p className="text-muted-foreground">
              {status.entries.length === 0
                ? "Ninguém entrou pela senha."
                : `Entraram pela senha: ${status.entries.map((e) => `${e.name ?? "?"} (${fmtWhen(e.entered_at)})`).join(", ")}`}
            </p>
          )}
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground">
            Conferência sem senha: só quem tem a permissão de conferência conta.
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-7 ml-auto"
            onClick={() => setConfirmOpen(true)}
            disabled={generateMutation.isPending}
          >
            Ligar senha
          </Button>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={(open) => !generateMutation.isPending && setConfirmOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{passwordRequired ? "Gerar nova senha?" : "Ligar senha?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {passwordRequired
                ? "A senha atual deixa de valer e o bloqueio sai. Quem entrou pela senha atual vai precisar digitar a nova."
                : "Quem está logado sem a permissão de conferência poderá contar com a senha. Depois de ligada, a senha não se desliga."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={generateMutation.isPending}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); generateMutation.mutate(); }}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {passwordRequired ? "Gerar nova senha" : "Ligar senha"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <StockChecklistPasswordReveal password={revealed} onClose={() => setRevealed(null)} />
    </div>
  );
};
